import './Blocks.scss'

import {BoxBlock, BoxBlockType} from './BoxBlock'
import {ColumnsBlock, ColumnsBlockType} from './ColumnsBlock'
import {CtaBlock, CtaBlockType} from './CtaBlock'
import {CtaCampaignBlock, CtaCampaignBlockType} from './CtaCampaignBlock'
import {CtaGiftBlock, CtaGiftBlockType} from './CtaGiftBlock'
import {DownloadsBlock, DownloadsBlockType} from './DownloadsBlock'
import {FormBlock, FormBlockType} from './FormBlock'
import {ImageTextBlock, ImageTextBlockType} from './ImageTextBlock'
import {ImagesBlock, ImagesBlockType} from './ImagesBlock'
import {LinksBlock, LinksBlockType} from './LinksBlock'
import {ListBlock, ListBlockType} from './ListBlock'
import {NewsBlock, NewsBlockType} from './NewsBlock'
import {OverviewBlock, OverviewBlockType} from './OverviewBlock'
import {PartnersBlock, PartnersBlockType} from './PartnersBlock'
import {PersonBlock, PersonBlockType} from './PersonBlock'
import {ProductBlock, ProductBlockType} from './ProductBlock'
import React, {Fragment} from 'react'
import {TextBlock, TextBlockType} from './TextBlock'
import {
	TextCtaPreviewBlock,
	TextCtaPreviewBlockType
} from './TextCtaPreviewBlock'
import {VideoBlock, VideoBlockType} from './VideoBlock'
import {WorldBlock, WorldBlockType} from './WorldBlock'

import {CardSwitchType} from '../layout/cards/CardSwitch'
import {DOMAttrs} from '@codeurs/front'
import {Line} from '../ui/Line'
import {MainUrlTypes} from '../../types'
import {Theme} from 'views/Theme'
import {style} from '../../util/style'

const styles = style('blocks', ['block', 'line'])

export type BlockType =
	| {type: 'line'}
	| ({type: 'text'} & TextBlockType)
	| ({type: 'text-cta-preview'} & TextCtaPreviewBlockType)
	| ({type: 'columns'} & ColumnsBlockType)
	| ({type: 'imagetext'} & ImageTextBlockType)
	| ({type: 'images'} & ImagesBlockType)
	| ({type: 'video'} & VideoBlockType)
	| ({type: 'links'} & LinksBlockType)
	| ({type: 'cta'} & CtaBlockType)
	| ({type: 'ctacampaign'} & CtaCampaignBlockType)
	| ({type: 'ctagift'} & CtaGiftBlockType)
	| ({type: 'overview'} & OverviewBlockType)
	| ({type: 'box'} & BoxBlockType)
	| ({type: 'person'} & PersonBlockType)
	| ({type: 'form'} & FormBlockType)
	| ({type: 'product'} & ProductBlockType)
	| ({type: 'partners'} & PartnersBlockType)
	| ({type: 'news'} & NewsBlockType)
	| ({type: 'list'} & ListBlockType)
	| ({type: 'downloads'} & DownloadsBlockType)
	| ({type: 'worldmap'} & WorldBlockType)
	| {type: 'custom'; render: () => any}

export const Blocks: React.FC<{
	blocks: Array<BlockType>
	className?: string
	container?: boolean
	fullwidth?: boolean
	main_pages?: Array<MainUrlTypes>
	news?: Array<CardSwitchType>
}> = ({
	blocks,
	className,
	container = false,
	fullwidth = false,
	main_pages,
	news
}) => {
	if (!blocks || !Array.isArray(blocks) || blocks.length === 0) return null
	const nomartop =
		blocks[0].type === 'box' ||
		blocks[0].type === 'form' ||
		blocks[0].type === 'text-cta-preview'

	return (
		<div className={styles().with(className).mod({nomartop})}>
			{blocks.map((block) => {
				switch (block.type) {
					case 'line':
						return <LineBlock container={container} />
					case 'text':
						return <TextBlock {...block} container={container} />
					case 'text-cta-preview':
						return <TextCtaPreviewBlock {...block} container={container} />
					case 'columns':
						return <ColumnsBlock {...block} container={container} />
					case 'imagetext':
						return <ImageTextBlock {...block} container={container} />
					case 'images':
						return <ImagesBlock {...block} container={container} />
					case 'video':
						return <VideoBlock {...block} container={container} />
					case 'links':
						return <LinksBlock {...block} container={container} />
					case 'cta':
						return <CtaBlock {...block} container={container} />
					case 'ctacampaign':
						return <CtaCampaignBlock {...block} container={container} />
					case 'ctagift':
						return <CtaGiftBlock {...block} container={container} />
					case 'overview':
						return <OverviewBlock {...block} container={container} />
					case 'box':
						return <BoxBlock {...block} container={container} />
					case 'person':
						return (
							<PersonBlock
								{...block}
								container={container}
								fullwidth={fullwidth}
							/>
						)
					case 'form':
						return <FormBlock {...block} container={container} />
					case 'product':
						return <ProductBlock {...block} container={container} />
					case 'partners':
						return <PartnersBlock {...block} container={container} />
					case 'list':
						return <ListBlock {...block} container={container} />
					case 'downloads':
						return <DownloadsBlock {...block} container={container} />
					case 'worldmap':
						return <WorldBlock {...block} container={container} />
					case 'news':
						return (
							<NewsBlock
								{...block}
								container={container}
								main_pages={main_pages}
								news={news}
							/>
						)
					case 'custom':
						return block.render()
					default:
						return <p>Unknown block type: {(block as any).type}</p>
				}
			})}
		</div>
	)
}

export const Block: React.FC<
	DOMAttrs & {container?: boolean; sidebar?: boolean}
> = ({container, sidebar, children, ...props}) => {
	const ContainerTag = container ? Theme.Container : Fragment

	return (
		<div className={styles.block().mod({sidebar}).mergeProps(props)}>
			<ContainerTag>{children}</ContainerTag>
		</div>
	)
}

export const LineBlock: React.FC<{container?: boolean; sidebar?: boolean}> = ({
	container,
	sidebar
}) => (
	<Block className={styles.line()} container={container} sidebar={sidebar}>
		<Line />
	</Block>
)
